import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiUser, FiLock, FiServer, FiMail, FiGlobe, FiCreditCard, FiHelpCircle, FiBarChart3, FiShield, FiSettings } = FiIcons;

const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const handleLogin = (e) => {
    e.preventDefault();
    // In a real application, you would authenticate with your backend
    setIsLoggedIn(true);
  };

  const handleInputChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  const portalFeatures = [
    {
      icon: FiServer,
      title: 'Hosting management',
      description: 'Monitor and manage your hosting accounts, view usage statistics, and upgrade plans'
    },
    {
      icon: FiGlobe,
      title: 'Domain management',
      description: 'Manage domain renewals, DNS settings, and domain portfolio from one dashboard'
    },
    {
      icon: FiMail,
      title: 'Email administration',
      description: 'Create and manage email accounts, set up forwarding, and configure spam filters'
    },
    {
      icon: FiHelpCircle,
      title: 'Support tickets',
      description: 'Submit support requests, track ticket status, and access your support history'
    },
    {
      icon: FiCreditCard,
      title: 'Billing & renewals',
      description: 'View invoices, update payment methods, and manage automatic renewals'
    },
    {
      icon: FiBarChart3,
      title: 'Usage monitoring',
      description: 'Track bandwidth usage, storage consumption, and performance metrics'
    }
  ];

  const quickActions = [
    { icon: FiServer, title: 'View hosting status', action: 'Check uptime' },
    { icon: FiGlobe, title: 'Renew domains', action: 'Manage renewals' },
    { icon: FiMail, title: 'Create email account', action: 'Add email' },
    { icon: FiHelpCircle, title: 'Submit support ticket', action: 'Get help' }
  ];

  if (isLoggedIn) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-20 min-h-screen bg-gray-50"
      >
        {/* Dashboard Header */}
        <section className="bg-white border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Welcome back!</h1>
                <p className="text-gray-600 mt-1">Manage your hosting, domains, and support tickets</p>
              </div>
              <button
                onClick={() => setIsLoggedIn(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                Sign out
              </button>
            </div>
          </div>
        </section>

        {/* Dashboard Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Quick Actions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {quickActions.map((action, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                  >
                    <div className="bg-primary-100 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <SafeIcon icon={action.icon} className="w-6 h-6 text-primary-500" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{action.title}</h3>
                    <p className="text-sm text-primary-500 font-medium">{action.action}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Services Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Hosting Services */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiServer} className="w-6 h-6 text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Hosting services</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">Premium hosting</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Active</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Uptime: <span className="text-green-600 font-medium">99.9%</span></p>
                    <p>Storage used: <span className="font-medium">2.1 GB / 5 GB</span></p>
                  </div>
                </div>
              </motion.div>

              {/* Domain Services */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Domain services</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">yourbusiness.co.za</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">365 days</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Auto-renewal: <span className="text-green-600 font-medium">Enabled</span></p>
                    <p>DNS status: <span className="font-medium">Configured</span></p>
                  </div>
                </div>
              </motion.div>

              {/* Support Tickets */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Support tickets</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">Email setup assistance</span>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">In progress</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Response time: <span className="text-green-600 font-medium">&lt;30 min</span></p>
                    <p>Open tickets: <span className="font-medium">1</span></p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section with Login and Image Side by Side */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Login Form - Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
                <div className="text-center mb-8">
                  <div className="bg-primary-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiShield} className="w-8 h-8 text-primary-500" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Client portal
                  </h1>
                  <p className="text-gray-600">
                    Access your account dashboard
                  </p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email address
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={loginForm.email}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="password"
                        id="password"
                        name="password"
                        required
                        value={loginForm.password}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                        placeholder="Your password"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
                      <span className="ml-2 text-sm text-gray-600">Remember me</span>
                    </label>
                    <a href="#" className="text-sm text-primary-500 hover:text-primary-600">
                      Forgot password?
                    </a>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200"
                  >
                    Sign in to portal
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Need an account?{' '}
                    <a href="#" className="text-primary-500 hover:text-primary-600 font-medium">
                      Contact support
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Hero Content and Image - Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  Manage your digital services
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Access your hosting accounts, manage domains, submit support tickets, and monitor your services from one secure dashboard.
                </p>
              </div>
              
              <img
                src="https://media.phst.co.za/file/ph/primehost-client-portal.jpg"
                alt="Secure client portal dashboard for managing digital services"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portal Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Portal features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to manage your digital services in one place
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <div className="bg-primary-100 p-3 rounded-xl w-12 h-12 flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-primary-50 rounded-2xl p-8 text-center"
          >
            <SafeIcon icon={FiShield} className="w-12 h-12 text-primary-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Secure and reliable access
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Your client portal is protected with enterprise-grade security measures including SSL encryption, secure authentication, and regular security monitoring to keep your account information safe.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="flex items-center justify-center space-x-2">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">SSL encrypted</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <SafeIcon icon={FiSettings} className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">24/7 monitoring</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <SafeIcon icon={FiLock} className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Secure authentication</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ClientPortal;